import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { IconSvgComponent } from '../../../icon/icon.component';
import { DateNavigationService } from '../../services/date-navigation.service';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePickerForm, FormControlDirection } from '../../datepicker-range-advanced.interfaces';
import { NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { CalendarDatesSelected, DateSelectionService } from '../../services/date-selection.service';
import { RxSelectModule } from '../../../select';
import { Subject, filter, takeUntil } from 'rxjs';
import { SelectOption } from '@rx-applications/shared/utils';

@Component({
  imports: [CommonModule, FormsModule, ReactiveFormsModule, IconSvgComponent, RxSelectModule],
  standalone: true,
  selector: 'rx-calendar-navigation',
  templateUrl: './calendar-navigation.component.html',
  styleUrls: ['./calendar-navigation.component.scss'],
})
export class CalendarNavigationComponent implements OnInit, OnDestroy {
  @Input() leftStartDate: NgbDate;
  @Input() rightStartDate: NgbDate;
  @Input() datePickerInstance: NgbDatepicker;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() testIdPrefix: string;

  @Input() set selectedDateType(selectedDateType: string) {
    this.monthList = this.dateNavigationService.getMonthsList();
    this.yearList = this.dateNavigationService.getYearsList(this.minDate, this.maxDate);
  }

  @Input() set selectedDates(dates: CalendarDatesSelected) {
    if (this.isUpdateViewRequired(dates)) {
      this.updateMonthYearSelects(dates.startDate, dates.endDate);
      setTimeout(() => this.datePickerInstance.navigateTo(dates.startDate),0);
    }
  }

  dateNavigationService = inject(DateNavigationService);
  dateSelectionService = inject(DateSelectionService);

  form: FormGroup<DatePickerForm>;
  monthList: SelectOption[] = [];
  yearList: SelectOption[] = [];

  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.setDefaultForm();
    this.setFormControlSubscriptions();
  }

  setDefaultForm(): void {
    this.datePickerInstance.navigateTo({month: this.leftStartDate.month, year: this.leftStartDate.year})

    this.form = new FormGroup({
      leftMonth: new FormControl(this.leftStartDate.month),
      leftYear: new FormControl(this.leftStartDate.year),
      rightMonth: new FormControl(this.rightStartDate.month),
      rightYear: new FormControl(this.rightStartDate.year),
    });
  }

  setFormControlSubscriptions(): void {
    this.subscribeToFormControlChanges('leftMonth', 'left', this.handleMonthChange.bind(this));
    this.subscribeToFormControlChanges('rightMonth', 'right', this.handleMonthChange.bind(this));
    this.subscribeToFormControlChanges('leftYear', 'left', this.handleYearChange.bind(this));
    this.subscribeToFormControlChanges('rightYear', 'right', this.handleYearChange.bind(this));
  }

  subscribeToFormControlChanges(
    controlName: keyof DatePickerForm,
    direction: FormControlDirection,
    handler: (value: number, direction: FormControlDirection) => void
  ): void {
    this.form.get(controlName)?.valueChanges.pipe(
      takeUntil(this.destroyed$),
      filter((value: number) => this.hasFormControlValueChanged(controlName, value))
    )
    .subscribe((value: number) => handler(value, direction));
  }

  private hasFormControlValueChanged(controlName: string, value: number): boolean {
    return this.datePickerInstance && this.form.value[controlName] !== value;
  }

  private handleMonthChange(value: number, direction: FormControlDirection): void {
    const [leftDate, rightDate] = this.dateNavigationService.monthChange(this.datePickerInstance, value, direction);
    this.updateMonthYearSelects(leftDate, rightDate);
  }

  private handleYearChange(value: number, direction: FormControlDirection): void {
    const [leftDate, rightDate] = this.dateNavigationService.yearChange(this.datePickerInstance, value, direction);
    this.updateMonthYearSelects(leftDate, rightDate);
  }

  onArrowClick(direction: FormControlDirection): void {
    if (this.dateNavigationService.isArrowEnabled(this.datePickerInstance, direction)) {
      const [leftDate, rightDate] = this.dateNavigationService.navigate(this.datePickerInstance, direction);
      this.updateMonthYearSelects(leftDate, rightDate);
    }
  }

  updateMonthYearSelects(leftDate: NgbDate, rightDate: NgbDate): void {
    this.form.patchValue(
      {
        leftMonth: leftDate.month,
        leftYear: leftDate.year,
        rightMonth: leftDate.month === 12 ? 1 : leftDate.month + 1,
        rightYear: leftDate.month === 12 ? leftDate.year + 1 : leftDate.year,
      },
      { emitEvent: false }
    );
  }

  getLeftMonths(): SelectOption[] {
    const { leftYear, rightMonth, rightYear } = this.form.value;
    return this.dateNavigationService.getEnabledMonths(
      this.monthList,
      leftYear,
      this.minDate,
      this.maxDate,
    );
  }

  getRightMonths(): SelectOption[] {
    const { rightYear, leftMonth, leftYear } = this.form.value;
    return this.dateNavigationService.getEnabledMonths(
      this.monthList,
      rightYear,
      this.minDate,
      this.maxDate,
    );
  }

  getMobileMonths(): SelectOption[] {
    const { leftYear } = this.form.value;
    return this.dateNavigationService.getEnabledMonths(
      this.monthList,
      leftYear,
      this.minDate,
      this.maxDate,
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private isUpdateViewRequired(dates: CalendarDatesSelected): boolean {
    return this.form && dates?.startDate && dates?.endDate && !this.areDatesInRange(dates);
  }

  areDatesInRange(dates: CalendarDatesSelected): boolean {
    const { leftMonth, leftYear, rightMonth, rightYear } = this.form.value;
    const startYearMonth = `${dates.startDate.year}${dates.startDate.month}`;
    const endYearMonth = `${dates.endDate.year}${dates.endDate.month}`;

    const selectedMonths: string[] = [`${leftYear}${leftMonth}`, `${rightYear}${rightMonth}`];

    return selectedMonths.includes(startYearMonth) && selectedMonths.includes(endYearMonth);
  }
}
